import { useEffect, useMemo, useRef, useState } from 'react';
import { Form, Button, Row, Col, Alert, Spinner } from 'react-bootstrap';
import { useLocation } from 'react-router-dom';
import Section from '../components/Section';
import Seo from '../components/Seo';

const initialState = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  company: '',
  message: '',
};

const Contact = () => {
  const location = useLocation();
  const [values, setValues] = useState(initialState);
  const [errors, setErrors] = useState({});
  const [submitState, setSubmitState] = useState('idle');
  const [submitMessage, setSubmitMessage] = useState('');
  const firstNameRef = useRef(null);
  const isSubmitting = submitState === 'loading';

  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);

  useEffect(() => {
    if (searchParams.get('focus') === 'form' && firstNameRef.current) {
      firstNameRef.current.focus();
    }
  }, [searchParams]);

  const validate = () => {
    const nextErrors = {};
    if (!values.firstName.trim()) {
      nextErrors.firstName = 'First Name is required.';
    }
    if (!values.email.trim()) {
      nextErrors.email = 'Email is required.';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email)) {
      nextErrors.email = 'Enter a valid email address.';
    }
    if (!values.company.trim()) {
      nextErrors.company = 'Company Name is required.';
    }
    return nextErrors;
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setValues((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (isSubmitting) {
      return;
    }

    const nextErrors = validate();
    setErrors(nextErrors);

    if (Object.keys(nextErrors).length === 0) {
      setSubmitState('loading');
      setSubmitMessage('');

      try {
        const formattedEmailBody = [
          `First Name: ${values.firstName || '-'}`,
          `Last Name: ${values.lastName || '-'}`,
          `Email: ${values.email || '-'}`,
          `Phone: ${values.phone || '-'}`,
          `Company Name: ${values.company || '-'}`,
          `Message: ${values.message || '-'}`,
        ].join('\n');

        const response = await fetch('https://formsubmit.co/ajax/e0af0829f859f128db91d150e4caeb08', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({
            _subject: `SalesFractionPro: ${values.firstName} from ${values.company} sent an inquiry`,
            _captcha: 'false',
            name: `${values.firstName} ${values.lastName}`.trim(),
            email: values.email,
            message: formattedEmailBody,
          }),
        });

        const data = await response.json();

        if (!response.ok || (data && data.success === false)) {
          throw new Error(data?.message || 'Unable to send your inquiry. Please try again.');
        }

        setSubmitState('success');
        setSubmitMessage('Inquiry sent! We will get back to you shortly.');
        setValues(initialState);
      } catch (error) {
        setSubmitState('error');
        setSubmitMessage('Something went wrong while sending. Please try again.');
      }
    }
  };

  return (
    <>
      <Seo
        title="Contact | Fractional Sales VP for Industrial B2B Companies"
        description="Contact SalesFractionPro to send an inquiry directly to info@salesfractionpro.com."
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'ContactPage',
          name: 'Contact SalesFractionPro',
          description: 'Contact SalesFractionPro for fractional sales and commercial leadership support.',
        }}
      />

      <Section className="page-hero" variant="hero">
        <Row>
          <Col>
            <h1 style={{ fontSize: 60 }}>Contact</h1>
          </Col>
        </Row>
      </Section>

      <Section variant="alt">
        <Row className='gy-5'>
          <Col lg={7}>
            <p className="lead">I personally review all inquiries. All conversations are confidential.</p>
            <Form noValidate onSubmit={handleSubmit} className="contact-form" aria-label="Contact form">
              <Row className="g-3">
                <Col md={6}>
                  <Form.Group controlId="firstName">
                    <Form.Label>First Name*:</Form.Label>
                    <Form.Control
                      ref={firstNameRef}
                      type="text"
                      name="firstName"
                      value={values.firstName}
                      onChange={handleChange}
                      disabled={isSubmitting}
                      isInvalid={!!errors.firstName}
                      required
                    />
                    <Form.Control.Feedback type="invalid">{errors.firstName}</Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group controlId="lastName">
                    <Form.Label>Last Name:</Form.Label>
                    <Form.Control
                      type="text"
                      name="lastName"
                      value={values.lastName}
                      onChange={handleChange}
                      disabled={isSubmitting}
                      isInvalid={!!errors.lastName}
                    />
                    <Form.Control.Feedback type="invalid">{errors.lastName}</Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col md={12}>
                  <Form.Group controlId="email">
                    <Form.Label>Email*:</Form.Label>
                    <Form.Control
                      type="email"
                      name="email"
                      value={values.email}
                      onChange={handleChange}
                      disabled={isSubmitting}
                      isInvalid={!!errors.email}
                      required
                    />
                    <Form.Control.Feedback type="invalid">{errors.email}</Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group controlId="phone">
                    <Form.Label>Phone:</Form.Label>
                    <Form.Control type="tel" name="phone" value={values.phone} onChange={handleChange} disabled={isSubmitting} />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group controlId="company">
                    <Form.Label>Company Name*:</Form.Label>
                    <Form.Control
                      type="text"
                      name="company"
                      value={values.company}
                      onChange={handleChange}
                      disabled={isSubmitting}
                      isInvalid={!!errors.company}
                      required
                    />
                    <Form.Control.Feedback type="invalid">{errors.company}</Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col md={12}>
                  <Form.Group controlId="message">
                    <Form.Label>Message:</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={5}
                      name="message"
                      value={values.message}
                      onChange={handleChange}
                      disabled={isSubmitting}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <div className="form-actions">
                <Button type="submit" variant="primary" size="lg" disabled={isSubmitting}>
                  {isSubmitting ? (
                    <>
                      <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" />
                      Sending...
                    </>
                  ) : 'Submit'}
                </Button>
              </div>

              {submitState === 'success' ? (
                <Alert variant="success" className="mt-3">
                  {submitMessage}
                </Alert>
              ) : null}
              {submitState === 'error' ? (
                <Alert variant="danger" className="mt-3">
                  {submitMessage}
                </Alert>
              ) : null}
            </Form>
          </Col>
          <Col lg={5}>
            <div className="contact-details">
              <p>Or email directly: info@salesfractionpro.com</p>
              <p className='mb-0'>In-person support available for Toronto-area clients; remote and hybrid engagements globally.</p>
            </div>
          </Col>
        </Row>
      </Section>
    </>
  );
};

export default Contact;
